import { localStore, sessionEpoch, markSignedOut } from 'src/utils/userSession';

import {
  SIGNED_IN_HINT_KEY,
  LEGACY_WALLET_TOKEN_KEY,
  LEGACY_USER_ACCESS_TOKEN_KEY,
  LEGACY_USER_REFRESH_TOKEN_KEY,
} from 'src/constants';

// ----------------------------------------------------------------------

/**
 * Every key a signed-in person leaves behind in this browser's storage.
 *
 * The session itself is the api's cookie since the single sign-in (api#454) — not here, and not
 * readable from here. What storage holds is the hint that this browser signed in, and whatever a
 * build from before left: the account's two tokens (until api#454) and the wallet session's token
 * (until the e-mail-only sign-in, api#402). Signing out empties all of it — clearing only the hint
 * would leave the next person on a shared laptop holding the previous person's tokens.
 */
export const SESSION_STORAGE_KEYS = [
  LEGACY_WALLET_TOKEN_KEY,
  LEGACY_USER_ACCESS_TOKEN_KEY,
  LEGACY_USER_REFRESH_TOKEN_KEY,
  SIGNED_IN_HINT_KEY,
] as const;

/** The tokens a build from before kept in storage: dropped at start, never sent (api#402 step 2, api#454). */
export const LEGACY_TOKEN_KEYS = [
  LEGACY_WALLET_TOKEN_KEY,
  LEGACY_USER_ACCESS_TOKEN_KEY,
  LEGACY_USER_REFRESH_TOKEN_KEY,
] as const;

/** Removes every session key. Takes the store so the rule is checkable without a browser. */
export function clearStoredSessions(store: Pick<Storage, 'removeItem'>): void {
  SESSION_STORAGE_KEYS.forEach((key) => store.removeItem(key));
}

/**
 * Drops the tokens a browser kept from before the single sign-in: the wallet session's (api#402,
 * step 2) and the account's two (api#454).
 *
 * The storefront sends no token any more — the session is the api's cookie — so a token under any
 * of these keys is a session nothing here can use or end properly; left in place it would keep a
 * person "signed in" in the eyes of an old build, with no way to sign out of it. The api ended the
 * wallet sessions in step 3 of api#402; the account's tokens stay valid for their day and are
 * simply never sent. Returns whether one was found, so the caller can say so in a log or a test.
 */
export function dropLegacyTokens(store: Pick<Storage, 'getItem' | 'removeItem'>): boolean {
  try {
    let held = false;

    for (const key of LEGACY_TOKEN_KEYS) {
      if (store.getItem(key) !== null) {
        held = true;
        store.removeItem(key);
      }
    }

    return held;
  } catch {
    // A browser that blocks storage holds no token to drop — and must not lose the whole app over it.
    return false;
  }
}

/**
 * Signing out, as far as this browser's storage is concerned: the hint goes, every session read
 * starts over (`markSignedOut`), and every key of a session goes with it. The api's cookie is ended
 * by the api itself (`POST /user/auth/logout` answers with the cookie cleared); the caller is
 * responsible for emptying the query cache as well.
 */
export function clearAllSessions(): void {
  markSignedOut();
  const store = localStore();

  if (store) clearStoredSessions(store);
}

// ----------------------------------------------------------------------

/**
 * Which session a read made right now speaks for (app#248).
 *
 * The reads are cached under this value: a session that ends and another one that begins in the
 * same tab — no reload in between — are never served each other's deals. Before it, the next
 * person to sign in on a shared device was shown the previous person's rentals and purchases while
 * their own lists were asked for, and kept seeing them when their own answer was a refusal.
 *
 * Since the single sign-in there is no token to fingerprint (api#454): the value is the tab's
 * epoch — one more at every sign-in and sign-out, in this tab or another one — which tells two
 * sessions of one tab apart and says nothing about either.
 */
export function requestSession(): string {
  return `epoch:${sessionEpoch()}`;
}
